import { defineStore } from 'pinia'
import { Network, type NetworkStatus } from '@capacitor/network'
import type { Plugin, PluginListenerHandle } from '@capacitor/core'
import { ref, computed, watch } from 'vue'
import { useLogger } from '../composables/useLogger'
import { useSupabaseClientStore, SUPABASE_HEALTH_EVENT_NAME } from '../stores/supaBaseClientStore'

// Constants
const CHECK_INTERVAL = 5*60*1000; // 5 minutes
// const CHECK_INTERVAL = 10*1000; // 10 seconds

// Define Capacitor types
declare global {
  interface Window {
    Capacitor?: {
      isNative: boolean
      Plugins?: {
        Network?: Plugin
      }
    }
  }
}

type HeartbeatCallback = () => Promise<void> | void
type ConnectionState = 'connected' | 'disconnected' | 'recovering' | 'flaky'

export const useHeartbeatStore = defineStore('heartbeat', () => {
  const logger = useLogger()
  const supabaseClientStore = useSupabaseClientStore()
  
  // State
  const connectionState = ref<ConnectionState>('connected')
  const isOnline = ref(true)
  const lastChecked = ref(new Date())
  const registeredCallbacksConnectivityRestored = ref<HeartbeatCallback[]>([])
  const registeredCallbacksConnectivityLost = ref<HeartbeatCallback[]>([])
  const checkInterval = ref<NodeJS.Timer | null>(null)
  const networkListener = ref<PluginListenerHandle | null>(null)
  const failureCount = ref(0)
  const lastFailure = ref<Date | null>(null)
  const isExecutingCallbacks = ref(false)

  // Getters
  const connectionQuality = computed((): 'good' | 'poor' | 'none' => {
    if (connectionState.value === 'connected' && supabaseClientStore.isHealthy) return 'good'
    if (connectionState.value === 'flaky') return 'poor'
    return 'none'
  })

  const isRecovering = computed((): boolean => {
    return connectionState.value === 'recovering'
  })

  const isFlaky = computed((): boolean => {
    return connectionState.value === 'flaky'
  })

  // Network Check Methods
  async function checkConnectivity() {
    if (!canPerformCheck()) return
    
    try {
      const previousState = {
        wasOnline: isOnline.value,
        state: connectionState.value
      }
      
      await performNetworkChecks(previousState)
    } catch (error) {
      logger.logErrorCaught('Heartbeat: Network status check failed', 'HeartbeatStore.ts', error)
      await handleOffline()
    }
  }

  function canPerformCheck(): boolean {
    if (!process.client) {
      // logger.debug('Heartbeat: Skipping check - not in client')
      return false
    }
    
    if (isExecutingCallbacks.value) {
      // logger.debug('Heartbeat: Skipping check - callbacks in progress')
      return false
    }

    return true
  }

  async function performNetworkChecks(previousState: { wasOnline: boolean }) {
    if (await checkCapacitorNetwork(previousState)) return
    if (await checkBrowserNetwork(previousState)) return
    await performFetchCheck(previousState)
  }

  async function checkCapacitorNetwork(previousState: { wasOnline: boolean }): Promise<boolean> {
    if (!window?.Capacitor?.isNative) return false

    try {
      const status = await Network.getStatus()
      // logger.debug('Heartbeat: Capacitor network status:', status)
      
      if (status.connected) {
        await handleOnline(previousState)
        return true
      } else {
        await handleOffline()
        return true
      }
    } catch (e) {
      logger.logErrorCaught('Capacitor network check failed', 'HeartbeatStore.ts', e)
      return false
    }
  }

  async function checkBrowserNetwork(previousState: { wasOnline: boolean }): Promise<boolean> {
    if (navigator.onLine) {
      // logger.debug('Heartbeat: Browser reports online, verifying with fetch')
      return false
    } else {
      // logger.debug('Heartbeat: Browser reports offline')
      await handleOffline()
      return true
    }
  }

  async function performFetchCheck(previousState: { wasOnline: boolean }) {
    try {
      // this can throw an error if the fetch fails - means offline
      const response = await fetchWithTimeout()
      if (response) {
        // logger.debug('Heartbeat: Fetch check succeeded')
        await handleOnline(previousState)
      } else {
        // logger.debug('Heartbeat: Fetch check failed - no response')
        await handleOffline()
      }
    } catch (error) {
      // means that the fetch failed, so we are offline - this is an expected return
      logger.warn('Heartbeat: Fetch check failed - no response')
      await handleOffline()
    }
  }

  /**
   * Fetch a response from a URL with a timeout
   * @returns the response if succes, throws an error if not
   */
  async function fetchWithTimeout(): Promise<Response | null> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 60000)

    try {
      const response = await fetch('https://www.gstatic.com/generate_204', {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
        mode: 'no-cors',
      })
      clearTimeout(timeoutId)
      lastChecked.value = new Date()
      return response
    } catch (error) {
      clearTimeout(timeoutId)
      throw error
    }
  }

  // State Management Methods
  async function handleOnline(previousState: { wasOnline: boolean }) {
    // logger.debug('Heartbeat: Handling online state')
    failureCount.value = 0
    lastFailure.value = null

    if (!previousState.wasOnline) {
      logger.info('Heartbeat: Connection restored, executing callbacks')
      connectionState.value = 'recovering'
      isOnline.value = true
      
      // Notify Supabase of network state change
      supabaseClientStore.handleNetworkStateChange(true)
      
      // Check Supabase health immediately when connection is restored
      try {
        const isHealthy = await supabaseClientStore.checkHealthImmediately()
        if (isHealthy) {
          logger.info('Heartbeat: Supabase connection verified after network recovery')
        } else {
          logger.warn('Heartbeat: Immediate health check failed after network recovery, starting recovery process')
          await supabaseClientStore.checkIfSupabaseProblem()
        }
      } catch (error) {
        logger.warn('Heartbeat: Failed to verify Supabase health after network recovery', { error })
        await supabaseClientStore.checkIfSupabaseProblem()
      }

      await executeCallbacksConnectivityRestoredSafely()
      
      // logger.debug('Heartbeat: Connection fully restored')
      connectionState.value = 'connected'
    } else {
      isOnline.value = true
      connectionState.value = 'connected'
      // Notify Supabase of network state change
      supabaseClientStore.handleNetworkStateChange(true)
    }
  }

  async function handleOffline() {
    const wasOnline = isOnline.value;  // Remember previous state
    
    failureCount.value++
    lastFailure.value = new Date()
    
    connectionState.value = failureCount.value >= 3 ? 'flaky' : 'disconnected'
    isOnline.value = false
    lastChecked.value = new Date()
    

    if (wasOnline) {
      // only execute callbacks and supabase state change if we're transitioning from online to offline
      supabaseClientStore.handleNetworkStateChange(false)
      await executeCallbacksConnectivityLostSafely()
    }
    
    logger.warn(`Heartbeat: Connection lost (failure #${failureCount.value})`)
  }

  // Callback Management Methods
  async function executeCallbacksConnectivityRestoredSafely() {
    logger.debug('Heartbeat: Executing restored callbacks safely')
    isExecutingCallbacks.value = true
    try {
      await executeCallbacksConnectivityRestored()
    } finally {
      isExecutingCallbacks.value = false
    }
  }

  async function executeCallbacksConnectivityRestored() {
    logger.debug('Heartbeat: Executing restored callbacks')
    for (const callback of registeredCallbacksConnectivityRestored.value) {
      try {
        logger.debug('Heartbeat: Executing callback:', callback.name)
        await callback()
      } catch (error) {
        logger.logErrorCaught('Heartbeat: Failed to execute heartbeat callback', 'HeartbeatStore.ts', error)
      }
    }
  }


  async function executeCallbacksConnectivityLostSafely() {
    logger.debug('Heartbeat: Executing lost callbacks')
    isExecutingCallbacks.value = true
    try {
      await executeCallbacksConnectivityLost()
    } finally {
      isExecutingCallbacks.value = false
    }
  }

  async function executeCallbacksConnectivityLost() {
    logger.debug('Heartbeat: Executing lost callbacks')
    for (const callback of registeredCallbacksConnectivityLost.value) {
      try {
        await callback()
      } catch (error) {
        logger.logErrorCaught('Heartbeat: Failed to execute heartbeat callback', 'HeartbeatStore.ts', error)
      }
    }
  } 

  // Monitoring Methods
  function startMonitoring() {
    // logger.debug('Heartbeat: Starting monitoring')
    if (checkInterval.value) {
      stopMonitoring()
    }
    
    setupNetworkListeners()
    checkConnectivity()
    
    checkInterval.value = setInterval(() => {
      checkConnectivity()
    }, CHECK_INTERVAL)
  }

  function stopMonitoring() {
    // logger.debug('Heartbeat: Stopping monitoring')
    if (checkInterval.value) {
      clearInterval(checkInterval.value as unknown as number)
      checkInterval.value = null
    }

    cleanupNetworkListeners()
  }

  // Network Listener Methods
  async function setupNetworkListeners() {
    if (!process.client) return

    try {
      if (window?.Capacitor?.isNative) {
        networkListener.value = await Network.addListener('networkStatusChange', 
          (status: NetworkStatus) => {
            if (status.connected) {
              checkConnectivity()
            } else {
              handleOffline()
            }
          }
        )
      } else {
        window.addEventListener('online', checkConnectivity)
        window.addEventListener('offline', handleOffline)
      }
    } catch (e) {
      logger.logErrorCaught('Heartbeat: Failed to setup network listeners', 'HeartbeatStore.ts', e)
    }
  }

  function cleanupNetworkListeners() {
    if (!process.client) return

    try {
      if (window?.Capacitor?.isNative && networkListener.value) {
        networkListener.value.remove()
        networkListener.value = null
      } else {
        window.removeEventListener('online', checkConnectivity)
        window.removeEventListener('offline', handleOffline)
      }
    } catch (e) {
      logger.logErrorCaught('Heartbeat: Failed to cleanup network listeners', 'HeartbeatStore.ts', e)
    }
  }

  // Callback Registration Methods
  function registerCallbackConnectivityRestored(callback: HeartbeatCallback) {
    if (!registeredCallbacksConnectivityRestored.value.includes(callback)) {
      registeredCallbacksConnectivityRestored.value.push(callback)
      // logger.debug('Heartbeat: Registered new heartbeat callback')
    }
  }

  function unregisterCallbackConnectivityRestored(callback: HeartbeatCallback) {
    const index = registeredCallbacksConnectivityRestored.value.indexOf(callback)
    if (index > -1) {
      registeredCallbacksConnectivityRestored.value.splice(index, 1)
      // logger.debug('Heartbeat: Unregistered heartbeat callback')
    }
  }

  function registerCallbackConnectivityLost(callback: HeartbeatCallback) {
    if (!registeredCallbacksConnectivityLost.value.includes(callback)) {
      registeredCallbacksConnectivityLost.value.push(callback)
    }
  }

  function unregisterCallbackConnectivityLost(callback: HeartbeatCallback) {
    const index = registeredCallbacksConnectivityLost.value.indexOf(callback)
    if (index > -1) {
      registeredCallbacksConnectivityLost.value.splice(index, 1)
      // logger.debug('Heartbeat: Unregistered heartbeat callback')
    }
  }
  
  

  // Watch Supabase health changes
  watch(() => supabaseClientStore.isHealthy, async (isHealthy) => {
    if (!isHealthy) {
      logger.warn('Heartbeat: Supabase became unhealthy')
      await handleOffline()
    }
  })

  // Initialize monitoring if we're on the client side
  if (process.client) {
    // logger.debug('Heartbeat: Initializing store')
    startMonitoring()
  }

  // Public interface
  return {
    // State
    connectionState,
    isOnline,
    lastChecked,
    // Getters
    connectionQuality,
    isRecovering,
    isFlaky,
    // Methods
    registerCallbackConnectivityRestored,
    unregisterCallbackConnectivityRestored,
    registerCallbackConnectivityLost,
    unregisterCallbackConnectivityLost,
  }
})

export interface HeartbeatStore {
  connectionState: ConnectionState
  isOnline: boolean
  lastChecked: Date
  connectionQuality: 'good' | 'poor' | 'none'
  isRecovering: boolean
  isFlaky: boolean
  registerCallbackConnectivityRestored: (callback: HeartbeatCallback) => void
  unregisterCallbackConnectivityRestored: (callback: HeartbeatCallback) => void
  registerCallbackConnectivityLost: (callback: HeartbeatCallback) => void
  unregisterCallbackConnectivityLost: (callback: HeartbeatCallback) => void
} 